import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { useCallback } from "react";
import { API_BASE_URL } from "../constants/apiBase";
import { getAppCheckToken, getIdToken, hasFirebaseUser } from "../firebase";

const API_KEY = process.env.REACT_APP_API_KEY;

/** Feil fra TV-endepunktene, med norsk tekst som kan vises til brukeren. */
export class TvApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

const fallbackMessage = (status: number) =>
  status === 401 || status === 403
    ? "Du har ikke tilgang til dette."
    : status === 502
      ? "AI-tjenesten svarte ikke. Prøv igjen om litt."
      : "Noe gikk galt. Prøv igjen.";

/** Leser `{ message }` fra svaret, også når svaret er en blob. */
const errorMessage = async (error: AxiosError): Promise<string> => {
  const status = error.response?.status ?? 0;
  let data: any = error.response?.data;
  if (data instanceof Blob) {
    try {
      data = JSON.parse(await data.text());
    } catch {
      data = null;
    }
  }
  return typeof data?.message === "string" && data.message
    ? data.message
    : fallbackMessage(status);
};

interface RequestOptions {
  /** Svaret er en fil (f.eks. JPEG fra preview). */
  blob?: boolean;
  /** Accept for filer (standard image/jpeg), f.eks. application/pdf. */
  accept?: string;
  /** Ekstra headere, f.eks. X-Client-Platform. */
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/**
 * Kall mot TV-endepunktene med de vanlige headerne (som `useFetch`), men som
 * kaster `TvApiError` med `message` fra backend og kan hente filer.
 */
const useTvApi = () =>
  useCallback(
    async <T = any>(
      url: string,
      method: Method,
      payload?: unknown,
      options: RequestOptions = {},
    ): Promise<T> => {
      const appCheckToken = await getAppCheckToken();
      const config = async (refresh: boolean): Promise<AxiosRequestConfig> => {
        const token = await getIdToken(refresh);
        return {
          url: `${API_BASE_URL}${url}`,
          method,
          data: payload,
          signal: options.signal,
          responseType: options.blob ? "blob" : "json",
          headers: {
            ...options.headers,
            accept: options.blob
              ? (options.accept ?? "image/jpeg")
              : "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(appCheckToken ? { "X-Firebase-AppCheck": appCheckToken } : {}),
            "Api-key": API_KEY || "",
          },
        };
      };
      try {
        try {
          return (await axios(await config(false))).data as T;
        } catch (first) {
          if (
            first instanceof AxiosError &&
            first.response?.status === 401 &&
            hasFirebaseUser()
          ) {
            return (await axios(await config(true))).data as T;
          }
          throw first;
        }
      } catch (error) {
        if (axios.isCancel(error)) throw error;
        if (error instanceof AxiosError) {
          throw new TvApiError(
            error.response
              ? await errorMessage(error)
              : "Fikk ikke kontakt med serveren. Sjekk nettet og prøv igjen.",
            error.response?.status ?? 0,
          );
        }
        throw error;
      }
    },
    [],
  );

export const isAbort = (error: unknown) => axios.isCancel(error);

export const messageOf = (error: unknown) =>
  error instanceof Error && error.message
    ? error.message
    : "Noe gikk galt. Prøv igjen.";

export default useTvApi;
